<template>
  <div
    v-click-outside="closeDropdown"
    class="v2-multiselect"
  >
    <!-- Trigger: shows tags or placeholder -->
    <div
      class="v2-multiselect__trigger"
      :class="{
        'v2-multiselect__trigger--disabled': disabled,
        'v2-multiselect__trigger--open': isOpen,
      }"
      @click="toggleDropdown"
    >
      <div
        v-if="value && value.length"
        class="v2-multiselect__tags"
      >
        <span
          v-for="item in value"
          :key="item"
          class="v2-multiselect__tag"
        >
          <span class="v2-multiselect__tag-text">{{ item }}</span>
          <button
            class="v2-multiselect__tag-remove"
            type="button"
            @click.stop="removeItem(item)"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
              />
            </svg>
          </button>
        </span>
      </div>
      <span
        v-else
        class="v2-multiselect__placeholder"
      >
        {{ placeholder }}
      </span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="v2-multiselect__chevron"
        :class="{ 'v2-multiselect__chevron--open': isOpen }"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>

    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      class="v2-multiselect__menu"
    >
      <div
        v-if="searchable"
        class="v2-multiselect__search"
      >
        <input
          ref="searchInput"
          v-model="searchText"
          class="v2-multiselect__search-input"
          placeholder="Search..."
          @input="onSearch"
        >
      </div>
      <div class="v2-multiselect__options">
        <template v-if="hasOptions">
          <template v-for="(group, gIdx) in filteredGroups">
            <div
              v-if="group.options.length"
              :key="'g-' + gIdx"
            >
              <div class="v2-multiselect__group-header">
                {{ group.title }}
              </div>
              <button
                v-for="option in group.options"
                :key="group.title + '-' + option"
                class="v2-multiselect__option"
                :class="{ 'v2-multiselect__option--selected': isSelected(option) }"
                @click="toggleOption(option)"
              >
                <span class="v2-multiselect__option-check">
                  <svg
                    v-if="isSelected(option)"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span class="v2-multiselect__option-label">{{ option }}</span>
              </button>
            </div>
          </template>
        </template>
        <div
          v-else
          class="v2-multiselect__empty"
        >
          {{ noDataText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'V2MultiSelect',
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = e => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      },
    },
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    optionGroups: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: 'No results found.',
    },
  },
  data() {
    return {
      isOpen: false,
      searchText: '',
    }
  },
  computed: {
    filteredGroups() {
      if (!this.searchText) return this.optionGroups
      const q = this.searchText.toLowerCase()
      return this.optionGroups.map(group => ({
        title: group.title,
        options: group.options.filter(opt => String(opt).toLowerCase().includes(q)),
      }))
    },
    hasOptions() {
      return this.filteredGroups.some(g => g.options.length > 0)
    },
  },
  watch: {
    isOpen(val) {
      if (val && this.searchable) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
      if (!val) {
        this.searchText = ''
        this.$emit('search', '')
      }
    },
  },
  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen
      }
    },
    closeDropdown() {
      this.isOpen = false
    },
    isSelected(option) {
      return this.value.includes(option)
    },
    toggleOption(option) {
      let updated
      if (this.isSelected(option)) {
        updated = this.value.filter(v => v !== option)
      } else {
        updated = [...this.value, option]
      }
      this.$emit('input', updated)
      this.$emit('select', updated)
    },
    removeItem(item) {
      const updated = this.value.filter(v => v !== item)
      this.$emit('input', updated)
      this.$emit('select', updated)
    },
    onSearch() {
      this.$emit('search', this.searchText)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-multiselect {
  position: relative;

  // Clean opaque-white trigger — replaced the recessed-pocket glass recipe.
  &__trigger {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-2);
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    // Trigger — no `backdrop-filter`. Menu (below) keeps its blur because
    // only one is open at a time.
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    min-height: 38px;
    max-height: 120px;
    overflow-y: auto;

    &:hover:not(.v2-multiselect__trigger--open) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &--open {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__placeholder {
    color: var(--v2-muted-foreground);
    line-height: 22px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--v2-space-1);
    flex: 1;
    min-width: 0;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    padding: 2px 6px 2px 8px;
    background: var(--v2-foreground);
    color: var(--v2-background);
    border-radius: var(--v2-radius-full, 9999px);
    font-size: 0.8125rem;
    line-height: 1.4;
    white-space: nowrap;
  }

  &__tag-text {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tag-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    opacity: 0.7;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  &__chevron {
    flex-shrink: 0;
    margin-top: 3px;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  // Floating surface — near-opaque white so dark text stays readable when
  // the menu opens over arbitrary content (product images, dark cards).
  &__menu {
    position: absolute;
    top: calc(100% + var(--v2-space-1));
    left: 0;
    width: 100%;
    min-width: 200px;
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 1rem;
    box-shadow: var(--v2-glass-shadow-floating);
    z-index: 100;
    max-height: 300px;
    display: flex;
    flex-direction: column;
  }

  &__search {
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
    flex-shrink: 0;
  }

  &__search-input {
    width: 100%;
    padding: var(--v2-space-1) var(--v2-space-2);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-sm);
    outline: none;

    &:focus {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__options {
    overflow-y: auto;
    padding: var(--v2-space-1) 0;
  }

  &__group-header {
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-primary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    width: 100%;
    padding: var(--v2-space-1-5) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--selected {
      background: var(--v2-muted);
    }
  }

  &__option-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--v2-primary);
  }

  &__option-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }
}
</style>
